
import * as Prism from 'prismjs';
import React, {useEffect, useState} from "react";
import "./CustomScript.css";
import Editor from 'react-simple-code-editor';
import 'prismjs/components/prism-javascript';
import 'prismjs/themes/prism-tomorrow.css';
import Button from "react-bootstrap/Button";
import {faCircleNotch, faPlay, faSkullCrossbones} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import ReactJsonView from "@microlink/react-json-view";
import {ButtonToolbar} from "react-bootstrap";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import {faFloppyDisk, faTrashCan} from "@fortawesome/free-regular-svg-icons";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";


const baseScript = `
function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

function print(content) {
    setLogOutput(prev => [...prev, [getCurrentTimestamp(), content]]);
}
`;

const initialScript = `let devices = await bond.get("/devices");

for (const [deviceId, value] of Object.entries(devices)) {
  if(deviceId[0] === "_") continue;
  let device = await bond.get(\`/devices/\${deviceId}\`);
  print(\`\${deviceId}: \${device.name}\`);  
}`


window.abortController = null;

const CustomScript = ({bond, currentScript, onSave}) => {
    const [code, setCode] = React.useState(currentScript ? currentScript.code : initialScript);
    const [title, setTitle] = React.useState(currentScript ? currentScript.title : '');
    const [logOutput, setLogOutput] = useState([]);
    const [logOutputRendered, setLogOutputRendered] = useState([]);
    const [isRunning, setIsRunning] = useState(false);


    function getCurrentTimestamp() {
        const now = new Date();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        const milliseconds = String(now.getMilliseconds()).padStart(3, '0'); // Pad to 3 digits
        return `${hours}:${minutes}:${seconds}.${milliseconds}`;
    }

    useEffect(renderOutput, [logOutput]);

    useEffect(() => {
        if (!currentScript) return;
        setCode(currentScript.code)
        setTitle(currentScript.title)
    }, [currentScript])

    async function killScript() {
        if (window.abortController) {
            window.abortController.abort();
            setLogOutput(prev => [...prev, [getCurrentTimestamp(), "Killed.", "warning"]]);
        }
    }

    function injectAbortChecks(code) {
        const lines = code.split(';');

        let wrappedLines = [];
        let openBrackets = 0;
        let openParentheses = 0;

        for (let i = 0; i < lines.length; i++) {
            let line = lines[i];

            openParentheses += (line.match(/\(/g) || []).length;
            openParentheses -= (line.match(/\)/g) || []).length;
            openBrackets += (line.match(/\[/g) || []).length;
            openBrackets -= (line.match(/\]/g) || []).length;

            const insideMultiLineConstruct = openParentheses > 0 || openBrackets > 0;

            if (insideMultiLineConstruct) wrappedLines.push(`${line};`); else wrappedLines.push(`${line};\nif (window.abortController.signal.aborted) throw new Error('Script aborted');`);
        }

        return wrappedLines.join('');
    }


    async function runScript() {
        if (isRunning) return;
        localStorage.setItem('customScript', code);
        setLogOutput(prev => [...prev, [getCurrentTimestamp(), "Started.", "success"]]);
        setIsRunning(true)
        window.bond = bond;
        window.abortController = new AbortController();
        const fullCodeWithChecks = injectAbortChecks(code);
        console.debug("Injected Script:\n", fullCodeWithChecks)

        try {
            await eval(`(async () => {
                ${baseScript}
                ${fullCodeWithChecks}
            })()`);
        } catch (e) {
            if (e.message !== "Script aborted") {
                setLogOutput(prev => [...prev, [getCurrentTimestamp(), "Error:\n" + e, "warning"]]);
                console.error(e);
            }
        }
        window.abortController = null;
        setIsRunning(false);
        setLogOutput(prev => [...prev, [getCurrentTimestamp(), "Finished.", "danger"]]);
    }

    function renderOutput() {
        setLogOutputRendered(logOutput.map((log, index) => <div key={index} className={"outputLine " + log[2]}>
            <span className={"outputTimestamp"}>{log[0]}</span>
            <span className={"outputContent"}>{typeof log[1] === "object" ? <ReactJsonView
                src={log[1]}
                className={""}
                theme={"tomorrow"}
                name={false}
                displayObjectSize={false}
                displayDataTypes={false}
            /> : log[1]}</span>
        </div>))
    }

    function onCodeKeyDown(e) {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            runScript();
            e.preventDefault();
        }
        if (e.key === 'c' && e.ctrlKey) killScript();
        if (e.key === 'l' && e.ctrlKey) setLogOutput([]);
    }


    return (<>
        <div className={"editorBorder"}>
            <InputGroup>
                <Form.Control placeholder={"Sample Request"} value={title} onChange={(e) => {
                    setTitle(e.target.value)
                }}/>
                <Button disabled={title.length === 0} onClick={() => {
                    onSave(title, code)
                }}>
                    <FontAwesomeIcon icon={faFloppyDisk}/>
                </Button>
            </InputGroup>
            <Editor
                value={code}
                onValueChange={code => setCode(code)}
                padding={10}
                tabSize={4}
                onKeyDown={onCodeKeyDown}
                highlight={code => Prism.highlight(code, Prism.languages.js)
                    .split('\n')
                    .map((line, index) => {
                        return `<span><span class="lineNumber">${index + 1}</span><span class="lineContent">${line}</span></span>`
                    })
                    .join('\n')}
                style={{
                    fontFamily: '"Fira code", "Fira Mono", monospace', fontSize: 14,
                }}
            />
        </div>
        <ButtonToolbar
            className="justify-content-between scriptToolbar"
            aria-label="Toolbar with Button groups"
        >
            <ButtonGroup aria-label="First group">
                <Button onClick={() => {
                    setLogOutput([])
                }} variant={"outline-danger"} disabled={logOutput.length === 0}>
                    Clear <FontAwesomeIcon icon={faTrashCan}/>
                </Button>
            </ButtonGroup>
            <ButtonGroup aria-label="First group">
                <Button onClick={killScript} className={"float-end killBtn"} disabled={!isRunning} variant={"danger"}>
                    <FontAwesomeIcon icon={faSkullCrossbones}/>
                </Button>
                <Button onClick={runScript} disabled={isRunning} variant={"success"} className={"float-end"}>
                    Run <FontAwesomeIcon icon={isRunning ? faCircleNotch : faPlay}
                                         className={isRunning ? "fa-spin" : ""}/>
                </Button>
            </ButtonGroup>
        </ButtonToolbar>

        {logOutput.length ? <div className="logOutput">
            {logOutputRendered}
        </div> : <></>}
    </>);
}

export default CustomScript;